import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import { timeAgo } from "@/pages/admin/dashboard/types";
import type { AdminProfile, SellerStatus } from "@/pages/admin/dashboard/types";

interface SellersTabProps {
  profiles: AdminProfile[];
  onRefresh: () => void;
}

const STATUS_META: Record<
  SellerStatus,
  { label: string; badge: string; icon: string }
> = {
  none: { label: "Sin solicitar", badge: "bg-background-100 text-foreground-600", icon: "ri-question-line" },
  pending: { label: "Pendiente", badge: "bg-accent-100 text-accent-800", icon: "ri-time-line" },
  approved: { label: "Aprobado", badge: "bg-secondary-100 text-secondary-700", icon: "ri-checkbox-circle-line" },
  suspended: { label: "Suspendido", badge: "bg-accent-100 text-accent-800", icon: "ri-pause-circle-line" },
  rejected: { label: "Rechazado", badge: "bg-background-100 text-foreground-600", icon: "ri-close-circle-line" },
};

function initials(name: string | null, email: string | null): string {
  return (name ?? email ?? "U").charAt(0).toUpperCase();
}

export default function SellersTab({ profiles, onRefresh }: SellersTabProps) {
  const { showToast } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const sellers = useMemo(
    () =>
      profiles
        .filter((p) => p.account_type === "seller")
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ),
    [profiles]
  );

  const approve = async (seller: AdminProfile) => {
    setBusyId(seller.id);
    const { error } = await supabase.rpc("approve_seller", {
      user_uuid: seller.id,
    });
    setBusyId(null);
    if (error) {
      showToast(error.message || "No se pudo aprobar al vendedor.", "error");
      return;
    }
    showToast(`${seller.full_name ?? "Vendedor"} fue aprobado.`, "success");
    onRefresh();
  };

  const suspend = async (seller: AdminProfile) => {
    setBusyId(seller.id);
    const { error } = await supabase.rpc("suspend_seller", {
      user_uuid: seller.id,
      reason: "Suspensión administrativa",
    });
    setBusyId(null);
    if (error) {
      showToast(error.message || "No se pudo suspender al vendedor.", "error");
      return;
    }
    showToast(`${seller.full_name ?? "Vendedor"} fue suspendido.`, "success");
    onRefresh();
  };

  if (sellers.length === 0) {
    return (
      <EmptyState
        icon="ri-store-2-line"
        title="Sin vendedores"
        description="Aún no hay usuarios que hayan solicitado ser vendedores."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left">
        <thead>
          <tr className="border-b border-background-200/70 bg-background-100/60 text-xs font-semibold uppercase tracking-wide text-foreground-500">
            <th className="px-5 py-3">Vendedor</th>
            <th className="px-5 py-3">Estado</th>
            <th className="px-5 py-3">Verificado</th>
            <th className="px-5 py-3">Registro</th>
            <th className="px-5 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {sellers.map((seller) => {
            const meta = STATUS_META[seller.seller_status] ?? STATUS_META.none;
            const isApproved = seller.seller_status === "approved";
            return (
              <tr
                key={seller.id}
                className="border-b border-background-200/60 transition-colors last:border-0 hover:bg-background-100/40"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                      {initials(seller.full_name, seller.email)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground-950">
                        {seller.full_name ?? "Vendedor"}
                      </p>
                      <p className="truncate text-xs text-foreground-500">
                        {seller.email}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.badge}`}
                  >
                    <i className={`${meta.icon} text-sm`}></i>
                    {meta.label}
                  </span>
                </td>
                <td className="px-5 py-3">
                  {seller.seller_verified ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-secondary-700">
                      <i className="ri-verified-badge-fill text-secondary-600"></i>
                      Sí
                    </span>
                  ) : (
                    <span className="text-xs text-foreground-400">No</span>
                  )}
                </td>
                <td className="px-5 py-3 text-sm text-foreground-600">
                  {timeAgo(seller.created_at)}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {!isApproved && (
                      <button
                        type="button"
                        disabled={busyId === seller.id}
                        onClick={() => approve(seller)}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <i className="ri-check-line"></i>
                        Aprobar
                      </button>
                    )}
                    {isApproved && (
                      <button
                        type="button"
                        disabled={busyId === seller.id}
                        onClick={() => suspend(seller)}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <i className="ri-pause-circle-line"></i>
                        Suspender
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}